import { loadingSpinner } from '../utils/bookingUtils';
import { CHECKOUT_PAGE_PHASES } from '../constants/bookingConst';
import '../css/PosCheckout.css';

/**
 * The page around a checkout tab — the court-booking system's
 * CheckoutPageShell: a header naming whose checkout this is, the loading
 * spinner, and the "finished" / "no longer active" notices. The page itself
 * supplies only the checkout card.
 *
 * THE PAGE SCROLLS, not the card: the checkout is the whole of its tab, so
 * it is laid out as an ordinary page and grows to its content.
 *
 * @param {object} props
 * @param {string} props.phase - CHECKOUT_PAGE_PHASES value from useCheckoutPageSession.
 * @param {string} props.brandName - shown in the header
 * @param {string} [props.logoUrl] - shown beside the name, and in the spinner
 * @param {string} props.homeHref - where "Back to …" leads on a notice
 * @param {string} props.inactiveMessage - body of the "no longer active" notice.
 * @param {string} [props.className] - e.g. 'ps-kawnter' to brand the landing page's checkout
 * @param {React.ReactNode} props.children - the checkout card, shown while phase is ACTIVE.
 * @returns {JSX.Element}
 */
function PosCheckoutPageShell({
  phase, brandName, logoUrl = '', homeHref, inactiveMessage, className = '', children,
}) {
  if (phase === CHECKOUT_PAGE_PHASES.PROBING || phase === CHECKOUT_PAGE_PHASES.WAITING) {
    return loadingSpinner('Preparing your checkout', logoUrl);
  }

  /**
   * A card with a title, a line of text and a link back to the site.
   * @param {string} title
   * @param {string} message
   * @returns {JSX.Element}
   */
  const renderNotice = (title, message) => (
    <div className="ps-co ps-cp-notice">
      <h2 className="ps-co-title">{title}</h2>
      <p className="ps-cp-notice-text">{message}</p>
      <a className="ps-cp-link" href={homeHref}>Back to {brandName}</a>
    </div>
  );

  return (
    <div className={`ps-cp ${className}`.trim()}>
      <header className="ps-cp-header">
        <div className="ps-cp-brand">
          {logoUrl && <img src={logoUrl} alt="" className="ps-cp-logo" />}
          <span className="ps-cp-brand-name">{brandName}</span>
        </div>
      </header>

      <main className="ps-cp-main">
        {phase === CHECKOUT_PAGE_PHASES.ACTIVE && children}
        {phase === CHECKOUT_PAGE_PHASES.ENDED && renderNotice(
          "You're all set",
          'This checkout is finished. You can close this tab and go back to where you started.',
        )}
        {phase === CHECKOUT_PAGE_PHASES.INACTIVE && renderNotice(
          'This checkout is no longer active',
          inactiveMessage,
        )}
      </main>
    </div>
  );
}

export default PosCheckoutPageShell;
